import {AUTH_ACTION} from "../action-types";
import {OrganizationModel, UserModel} from "../../utils/types";
import {IRootState} from "../reducers";
import {AuthService} from "../../services/auth.service";
import {UserService} from "../../services/user.service";
import {abortPendingRequests} from "../../services/http.service";
import {ToastService} from "../../services/toast.service";
import {finishLoading, startLoading} from "./spinner.actions";

export const setToken = (token: string) => ({
  type: AUTH_ACTION.SET_TOKEN,
  token,
});

export const setOrganizations = (organizations: OrganizationModel[]) => ({
  type: AUTH_ACTION.SET_ORGANIZATIONS,
  organizations,
});

export const setActiveOrganization = (organization: OrganizationModel) => ({
  type: AUTH_ACTION.SET_ACTIVE_ORGANIZATION,
  organization,
});

export const setCurrentUser = (user: UserModel) => ({
  type: AUTH_ACTION.SET_CURRENT_USER,
  user,
});

export const setOrgSwitching = (switching: boolean) => ({
  type: AUTH_ACTION.SET_ORG_SWITCHING,
  switching,
});

export const orgContextChanged = () => ({
  type: AUTH_ACTION.ORG_CONTEXT_CHANGED,
});

/**
 * Switch the active organization.
 *
 * The backend scopes every org-scoped endpoint by the user's `default_org`, so
 * changing organizations is a two-part operation: persist the new default on
 * the profile, then re-establish the frontend's whole view of the world under
 * it. Dispatching `setActiveOrganization` alone (the old behaviour) only
 * renamed the org in the header — every mounted page kept the previous org's
 * campaigns, settings and permissions until a full page refresh.
 *
 * Order matters here:
 *  1. abandon in-flight requests so org A responses can't land in org B,
 *  2. persist `default_org` — from this point the backend answers as org B,
 *  3. abandon again, to catch anything fired while the PATCH was in flight,
 *  4. re-read `/admin/org` and `/admin/user` so permissions, roles and org
 *     settings are the new org's, not the ones cached at login,
 *  5. broadcast `ORG_CONTEXT_CHANGED`, which resets org-scoped slices and bumps
 *     `orgVersion` — the key that remounts the routed tree (see `OrgScope`).
 */
export const switchOrganization =
  (organization: OrganizationModel) =>
  async (dispatch, getState: () => IRootState): Promise<boolean> => {
    const {activeOrganization, orgSwitching} = getState().authReducer;

    if (orgSwitching || !organization?.uuid) {
      return false;
    }
    if (activeOrganization?.uuid === organization.uuid) {
      return true;
    }

    dispatch(setOrgSwitching(true));
    // Hold the spinner across the whole switch rather than letting each call
    // raise its own, so it doesn't flicker between the PATCH and the re-reads.
    dispatch(startLoading());

    try {
      abortPendingRequests();
      await UserService.modifyProfile({default_org: organization.uuid}, false);
      abortPendingRequests();

      const [organizations, user] = await Promise.all([
        AuthService.getMyOrganizations(false),
        UserService.getProfile(false),
      ]);

      dispatch(setOrganizations(organizations));
      dispatch(setCurrentUser(user));

      // `user.default_org` is what a full page refresh would have produced, and
      // it is the backend's answer on which org is actually active — if the
      // user has lost access to the one they clicked, this reflects that.
      // Never trust the copy held in the switcher list: it was read at login.
      const active =
        (user?.default_org && new OrganizationModel(user.default_org)) ||
        organizations.find((org) => org.uuid === organization.uuid) ||
        organization;
      dispatch(setActiveOrganization(active));

      dispatch(orgContextChanged());
      return true;
    } catch (err) {
      // Leave the previous organization active — a half-switched app is worse
      // than no switch at all.
      ToastService.showHttpError(err, "Switching organization failed");
      return false;
    } finally {
      dispatch(finishLoading());
      dispatch(setOrgSwitching(false));
    }
  };
